export type Stage = "upload" | "review" | "export";

export type ClipType = "Slate" | "Scene" | "Other";

export interface UploadedFile {
  id: string;
  file: File;
  name: string;
  sizeBytes: number;
  durationSec: number;
  url: string; // blob: URL for local preview
  remoteUrl?: string; // public Supabase URL
  storagePath?: string;
  isSlate: boolean;
  clipType?: ClipType;
  takeNumber?: number;
  trimStart: number;
  trimEnd: number;
  uploadProgress: number; // 0..100
  uploaded: boolean;
  customName?: string;
  otherLabel?: string; // user-selected/custom label when clipType === "Other"
  /**
   * User-editable label that becomes the final segment of the exported
   * filename: `First_Last_Project_Role_{label}`. When unset the legacy
   * clipType-based naming is used. Spaces are normalized to underscores.
   */
  label?: string;
}

export interface SessionMeta {
  firstName: string;
  lastName: string;
  projectName: string;
  roleName: string;
}

export function effectiveClipType(f: UploadedFile): ClipType {
  return f.clipType ?? (f.isSlate ? "Slate" : "Scene");
}

export function assignTakeNumbers(files: UploadedFile[]): UploadedFile[] {
  let take = 0;
  let changed = false;

  const nextFiles = files.map((file) => {
    if (effectiveClipType(file) !== "Scene") {
      if (file.takeNumber !== undefined) {
        changed = true;
        return { ...file, takeNumber: undefined };
      }
      return file;
    }

    take += 1;
    if (file.takeNumber === take) {
      return file;
    }

    changed = true;
    return { ...file, takeNumber: take };
  });

  return changed ? nextFiles : files;
}

export function sanitize(s: string): string {
  return (s || "")
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function buildFileName(
  meta: SessionMeta,
  f: UploadedFile,
): string {
  if (f.customName && f.customName.trim()) {
    const safe = sanitize(f.customName);
    return (safe || "Clip") + ".mp4";
  }
  const parts = [
    sanitize(meta.firstName) || "First",
    sanitize(meta.lastName) || "Last",
    sanitize(meta.projectName) || "Project",
    sanitize(meta.roleName) || "Role",
  ];
  if (f.label !== undefined) {
    const safeLabel = sanitize(f.label) || "Clip";
    parts.push(safeLabel);
    return parts.join("_") + ".mp4";
  }
  const type = effectiveClipType(f);
  if (type === "Scene") {
    parts.push("Scene");
    parts.push(`${f.takeNumber ?? 1}`);
  } else if (type === "Other") {
    const raw = (f.otherLabel ?? "").trim();
    const label = raw ? raw.replace(/[^a-zA-Z0-9]+/g, "") : "Other";
    parts.push(label || "Other");
  } else {
    parts.push(type);
  }
  return parts.join("_") + ".mp4";
}

export function formatTime(s: number): string {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
